import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns'

import { enUS as en, fr } from 'date-fns/locale'
import { capitalize, isNil, isString } from 'lodash'

import type { PaymentTermOption } from '@/shared/models/companyModel'

export type LocalizeMillisecondsToDaysProps = {
    distance: number
    locale?: string
}

export const localizeMillisecondsToDays = ({ distance, locale = 'en' }: LocalizeMillisecondsToDaysProps): string => {
    return capitalize(
        formatDistanceToNowStrict(new Date(Date.now() + distance), {
            addSuffix: true,
            locale: locale === 'fr' ? fr : en,
            unit: 'day'
        })
    )
}

export type LocalizeDateOptions = {
    format?: string
    hasHours?: boolean
}

export const localizeDate = (
    date: number | Date | string | undefined,
    options?: { locale?: string; dateFormatOptions?: LocalizeDateOptions }
): string | undefined => {
    if (isNil(date)) return undefined

    const locale = options?.locale || 'en'
    let dateFormatOptions = options?.dateFormatOptions || {}

    if (locale === 'fr') {
        dateFormatOptions = {
            ...dateFormatOptions,
            format: 'dd/MM/yyyy'
        }
    }

    let dateFormat = dateFormatOptions.format || 'yyyy-MM-dd'

    if (dateFormatOptions.hasHours) {
        dateFormat = `${dateFormat}, HH:mm:ss`
    }

    const formattedDate = new Date(date)

    if (isString(formattedDate)) {
        const [year, month, day] = formattedDate.slice(0, 10).split('-')
        return format(new Date(Number(year), Number(month) - 1, Number(day)), dateFormat)
    }

    return format(formattedDate, dateFormat)
}

export const dateToISOStringWithTimeZoneOffset = (date: Date): string => {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString()
}

export const isISODateString = (dateString: string) => {
    try {
        const parsedDate = parseISO(dateString)
        return isValid(parsedDate) && dateString === parsedDate.toISOString()
    } catch {
        return false
    }
}

export const calculateDueDate = (
    invoiceDate: Date,
    paymentTermType: PaymentTermOption,
    repaymentPeriod: number
): Date => {
    const dueDate = new Date(invoiceDate)

    if (paymentTermType === 'net') {
        dueDate.setDate(dueDate.getDate() + repaymentPeriod)
    } else if (paymentTermType === 'eom') {
        dueDate.setMonth(dueDate.getMonth() + 1, 0)
        dueDate.setDate(dueDate.getDate() + repaymentPeriod)
    } else if (paymentTermType === 'eomf') {
        dueDate.setMonth(dueDate.getMonth() + 2, 0)
        dueDate.setDate(dueDate.getDate() + repaymentPeriod)
    }

    return dueDate
}
